import { Injectable, computed, signal } from "@angular/core";
import { AttachedFile, MAX_FILES } from "../model/attachment.types";

/**
 * Scoped to ChatContainerComponent — tracks pending attachments and the current
 * threadId for the active chat session.
 * Each chat instance gets its own state; not provided at root.
 */
@Injectable()
export class AttachmentStateService {
  private readonly _files = signal<AttachedFile[]>([]);

  private readonly _threadId = signal<string>("");

  readonly files = this._files.asReadonly();

  readonly threadId = this._threadId.asReadonly();

  readonly isUploading = computed(() => this._files().some((f) => f.status === "uploading"));

  readonly hasFiles = computed(() => this._files().length > 0);

  readonly canAddMore = computed(() => this._files().length < MAX_FILES);

  /** Called by ChatContainerComponent whenever its threadId input changes. */
  setThreadId(id: string): void {
    this._threadId.set(id);
  }

  addFile(file: AttachedFile): void {
    this._files.update((files) => [...files, file]);
  }

  updateFile(localId: string, patch: Partial<AttachedFile>): void {
    this._files.update((files) => files.map((f) => (f.localId === localId ? { ...f, ...patch } : f)));
  }

  removeFile(localId: string): void {
    this._files.update((files) => files.filter((f) => f.localId !== localId));
  }

  clear(): void {
    this._files.set([]);
  }
}
